import React, { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import { useAppStore } from '@/lib/store';
import { isConvexEnabled } from '@/lib/convex';
import { WEBGPU_MODELS, resetWebGPUWorker } from '@/lib/ai/webgpu';

const PAGE_SIZE_INITIAL = 10;
const PAGE_SIZE_MORE = 10;

const CLOUDCALL_MODELS = [
  { id: '@cf/meta/llama-3.2-11b-vision-instruct',       name: 'Llama 3.2 11B Vision', badge: 'FAST', super: true },
  { id: '@cf/meta/llama-3.3-70b-instruct-fp8-fast',     name: 'Llama 3.3 70B',        badge: 'BEST', super: true },
  { id: '@cf/deepseek-ai/deepseek-r1-distill-qwen-32b', name: 'DeepSeek R1 32B',      badge: 'CODE', super: true },
  { id: '@cf/meta/llama-3.1-8b-instruct',        name: 'Llama 3.1 8B',   badge: null   },
  { id: '@cf/meta/llama-3.2-3b-instruct',        name: 'Llama 3.2 3B',   badge: null   },
  { id: '@cf/google/gemma-3-12b-it',             name: 'Gemma 3 12B',    badge: null   },
  { id: '@cf/mistral/mistral-7b-instruct-v0.1',  name: 'Mistral 7B',     badge: null   },
];

function formatCreatedDate(ts) {
  if (!ts) return '';
  const d = new Date(typeof ts === 'number' ? ts : Number(ts));
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function Sidebar() {
  const {
    isSidebarOpen, setSidebarOpen, activeView, setActiveView,
    conversations: localConversations, activeConversationId, setActiveConversation, setMessages,
    renameConversation, removeConversation, createConversation, migrateConversationId,
    userId, currentModel,
    chatMode, setChatMode,
    webgpuModelId, setWebgpuModelId,
    cloudcallModelId, setCloudcallModelId,
  } = useAppStore();

  const convexConversations = useQuery(
    api.conversations.list,
    isConvexEnabled ? { userId } : 'skip'
  );
  const conversations = (isConvexEnabled ? (convexConversations ?? localConversations) : localConversations) ?? [];

  const convexCreateConversation = useMutation(api.conversations.create);
  const convexUpdateConversation = useMutation(api.conversations.update);
  const convexRemoveConversation = useMutation(api.conversations.remove);
  const convexCreateMessage = useMutation(api.messages.create);

  const [shownCount, setShownCount] = useState(PAGE_SIZE_INITIAL);
  const [deleteCounters, setDeleteCounters] = useState({});
  const [renamingId, setRenamingId] = useState(null);
  const [renameInput, setRenameInput] = useState('');
  const [showModeExpanded, setShowModeExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const getNavClass = (viewName) =>
    activeView === viewName
      ? 'px-4 py-1.5 bg-[#323335] text-[#d5d6d9] font-medium border-l-[3px] border-transparent'
      : 'px-4 py-1.5 text-[#d5d6d9] hover:bg-[#2b2c2f] border-l-[3px] border-transparent';

  const navigate = (view) => {
    setActiveView(view);
    if (window.innerWidth < 768) setSidebarOpen(false);
  };

  const handleNewChat = async () => {
    let model = currentModel || 'claude-sonnet-4-6';
    if (chatMode === 'webgpu') model = webgpuModelId || 'webgpu';
    if (chatMode === 'cloudcall') model = cloudcallModelId || '@cf/meta/llama-3.2-3b-instruct';

    const localId = createConversation({ title: 'New Conversation', model, userId: userId || 'local', chatMode });
    setShownCount(PAGE_SIZE_INITIAL);
    navigate('chats');

    if (isConvexEnabled && userId) {
      try {
        const convexId = await convexCreateConversation({ userId, title: 'New Conversation', model, chatMode });
        // Move localId messages → convexId in store (handles race where user typed before Convex responded)
        migrateConversationId(localId, convexId);
        // Flush any messages that were already sent with the local ID
        const { conversationMessages } = useAppStore.getState();
        const preMigration = conversationMessages[convexId] || [];
        for (const msg of preMigration) {
          convexCreateMessage({
            conversationId: convexId,
            role: msg.role,
            content: msg.content || '',
            attachments: msg.attachments ?? [],
          }).catch(() => {});
        }
      } catch { /* stay local */ }
    }
  };

  const handleSelectConv = (convId) => {
    const conv = conversations.find((c) => (c._id ?? c.id) === convId);
    if (conv?.chatMode) setChatMode(conv.chatMode);
    setActiveConversation(convId);
    setActiveView('chats');
    if (window.innerWidth < 768) setSidebarOpen(false);
  };

  const startRename = (e, convId, currentTitle) => {
    e.stopPropagation();
    setRenamingId(convId);
    setRenameInput(currentTitle || '');
  };

  const commitRename = async (convId) => {
    const title = renameInput.trim();
    if (title) {
      renameConversation(convId, title);
      if (isConvexEnabled) {
        try { await convexUpdateConversation({ id: convId, title }); } catch { /* ignore */ }
      }
    }
    setRenamingId(null);
    setRenameInput('');
  };

  const handleDelete = async (e, convId) => {
    e.stopPropagation();
    const current = deleteCounters[convId] ?? 0;
    const next = current + 1;
    if (next >= 4) {
      removeConversation(convId);
      if (isConvexEnabled) {
        try { await convexRemoveConversation({ id: convId }); } catch { /* ignore */ }
      }
      setDeleteCounters((prev) => { const n = { ...prev }; delete n[convId]; return n; });
    } else {
      setDeleteCounters((prev) => ({ ...prev, [convId]: next }));
    }
  };

  const filteredConversations = searchQuery.trim()
    ? conversations.filter((c) => (c.title || '').toLowerCase().includes(searchQuery.toLowerCase()))
    : conversations;
  const visibleConversations = searchQuery.trim() ? filteredConversations : filteredConversations.slice(0, shownCount);
  const hasMore = !searchQuery.trim() && conversations.length > shownCount;

  const modeLabel = chatMode === 'cloudcall' ? 'CloudCall' : chatMode === 'webgpu' ? 'WebGPU' : 'API';
  const modeDot = chatMode === 'cloudcall' ? '#71e184' : chatMode === 'webgpu' ? '#b07ae8' : '#6498CC';

  return (
    <>
      {isSidebarOpen && (
        <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:-ml-[240px]'} absolute md:relative z-40 w-[240px] h-full bg-[#232426] border-r border-[#515153] flex-shrink-0 flex flex-col overflow-y-auto transform transition-all duration-300 ease-in-out`}>
        <div className="p-4 flex items-center gap-3">
          <div className="flex flex-1 h-[30px] rounded-full border border-[#515153] overflow-hidden">
            <button onClick={handleNewChat} className="flex-1 bg-[#323335] text-[#d5d6d9] flex items-center justify-center gap-1.5 px-3 text-[13px] font-medium hover:bg-[#3e3f42] transition-colors border-r border-[#515153]">
              <svg className="w-[15px] h-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
              Create
            </button>
            <button className="w-[32px] bg-[#323335] flex items-center justify-center text-[#b3b4b7] hover:bg-[#3e3f42] transition-colors">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
            </button>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="w-[30px] h-[30px] flex items-center justify-center text-[#b3b4b7] hover:text-white transition-colors bg-[#323335] rounded-md border border-[#515153]">
            <svg className="w-[15px] h-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7M3 12h18" /></svg>
          </button>
        </div>

        <nav className="flex flex-col text-[13.5px] mt-1">
          <a href="#" onClick={() => navigate('overview')} className={getNavClass('overview')}>Overview</a>
          <a href="#" onClick={() => { setActiveConversation(null); setMessages([]); navigate('chats'); }} className={getNavClass('chats')}>Chats</a>
          <a href="#" onClick={() => navigate('calendar')} className={getNavClass('calendar')}>Calendar</a>
          <a href="#" onClick={() => navigate('artifacts')} className={getNavClass('artifacts')}>Artifacts</a>
          <a href="#" onClick={() => navigate('customise')} className={getNavClass('customise')}>Customise</a>
          <a href="#" onClick={() => navigate('starred')} className={getNavClass('starred')}>Starred</a>
          <a href="#" onClick={() => navigate('connectors')} className={getNavClass('connectors')}>Connectors</a>

          {/* Recent Chats header */}
          <div className="mt-7 px-4 mb-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-white font-semibold text-[13.5px] tracking-wide">Recent Chats</span>
              <button onClick={handleNewChat} className="text-[#8f9196] hover:text-[#d5d6d9]" title="New Chat">
                <svg className="w-[15px] h-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
              </button>
            </div>

            {/* Search conversations */}
            <div className="relative mb-1.5">
              <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#515153]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                type="text"
                placeholder="Search chats…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#2b2c2f] border border-[#515153] rounded-md pl-6 pr-2 py-1 text-[11px] text-[#d5d6d9] placeholder-[#515153] outline-none focus:border-[#6498CC] transition-colors"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[#515153] hover:text-[#8f9196]">
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              )}
            </div>

            {/* How to use — compact mode selector */}
            <div className="flex items-center gap-1.5">
              <span className="text-[#515153] text-[10px]">via:</span>
              <button
                onClick={() => setShowModeExpanded((v) => !v)}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-[#515153] hover:border-[#8f9196] transition-colors"
              >
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: modeDot }} />
                <span className="text-[10px] font-semibold text-[#8f9196]">{modeLabel}</span>
                <svg className={`w-2.5 h-2.5 text-[#515153] transition-transform ${showModeExpanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6" /></svg>
              </button>
              <span className="text-[#515153] text-[10px] italic">for new chats</span>
            </div>
          </div>

          {/* Expanded mode panel */}
          {showModeExpanded && (
            <div className="px-4 mb-2 border border-[#515153] mx-2 rounded-md py-2">
              <p className="text-[#515153] text-[9px] uppercase tracking-widest mb-1.5">How to use</p>
              <div className="flex gap-1 mb-2">
                {[
                  { id: 'cloudcall', label: 'CloudCall', color: '#71e184', bg: '#1e3a2a', desc: 'Free · CF edge' },
                  { id: 'api',       label: 'API',       color: '#6498CC', bg: '#1e3a5f', desc: 'Your key' },
                  { id: 'webgpu',    label: 'WebGPU',    color: '#b07ae8', bg: '#2a1e3a', desc: 'Local' },
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setChatMode(m.id)}
                    className={`flex-1 flex flex-col items-center py-1.5 rounded text-[9px] font-bold transition-all border ${
                      chatMode === m.id
                        ? 'border-current'
                        : 'border-[#515153] text-[#8f9196] hover:border-[#8f9196]'
                    }`}
                    style={chatMode === m.id ? { color: m.color, backgroundColor: m.bg, borderColor: m.color } : {}}
                  >
                    <span>{m.label}</span>
                    <span className="font-normal opacity-70 text-[8px]">{m.desc}</span>
                  </button>
                ))}
              </div>

              {/* WebGPU model picker */}
              {chatMode === 'webgpu' && (
                <div>
                  <p className="text-[#515153] text-[9px] uppercase tracking-widest mb-1">Local Model</p>
                  {WEBGPU_MODELS.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => { if (webgpuModelId !== m.id) { resetWebGPUWorker(); setWebgpuModelId(m.id); } }}
                      className={`w-full flex items-center justify-between px-2 py-1 mb-0.5 rounded text-[11px] transition-all border ${
                        webgpuModelId === m.id
                          ? 'bg-[#2a1e3a] border-[#b07ae8] text-[#b07ae8]'
                          : 'bg-transparent border-[#515153] text-[#8f9196] hover:border-[#7a4a9a]'
                      }`}
                    >
                      <span className="font-medium truncate">{m.name}</span>
                      <div className="flex items-center gap-1 ml-1 flex-shrink-0">
                        {m.fast && <span className="text-[8px] bg-[#244c2a] text-[#71e184] px-1 py-0.5 rounded font-bold">FAST</span>}
                        <span className="text-[9px] opacity-60">{m.size}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* CloudCall model picker */}
              {chatMode === 'cloudcall' && (
                <div>
                  {/* SUPER tier */}
                  <div className="flex items-center gap-1.5 mb-1">
                    <p className="text-[9px] uppercase tracking-widest font-bold" style={{ color: '#ff4d00' }}>⚡ SUPER</p>
                    <span className="text-[8px] text-[#ff4d00] opacity-70">auto-routes · free forever</span>
                  </div>
                  {CLOUDCALL_MODELS.filter(m => m.super).map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setCloudcallModelId(m.id)}
                      className={`w-full flex items-center justify-between px-2 py-1 mb-0.5 rounded text-[11px] transition-all border ${
                        cloudcallModelId === m.id
                          ? 'border-[#ff4d00] text-[#ff6a2a]'
                          : 'border-[#515153] text-[#8f9196] hover:border-[#ff4d00] hover:text-[#ff6a2a]'
                      }`}
                      style={cloudcallModelId === m.id ? { backgroundColor: 'rgba(255,77,0,0.1)' } : {}}
                    >
                      <span className="font-bold truncate">{m.name}</span>
                      <span className={`text-[8px] px-1 py-0.5 rounded font-bold ml-1 flex-shrink-0 ${
                        m.badge === 'BEST' ? 'bg-[#3a1500] text-[#ff6a2a]' :
                        m.badge === 'CODE' ? 'bg-[#1e3a5f] text-[#6498CC]' :
                        'bg-[#244c2a] text-[#71e184]'
                      }`}>{m.badge}</span>
                    </button>
                  ))}
                  {/* Classic tier */}
                  <p className="text-[#515153] text-[9px] uppercase tracking-widest mt-2 mb-1">Classic</p>
                  {CLOUDCALL_MODELS.filter(m => !m.super).map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setCloudcallModelId(m.id)}
                      className={`w-full flex items-center justify-between px-2 py-1 mb-0.5 rounded text-[11px] transition-all border ${
                        cloudcallModelId === m.id
                          ? 'bg-[#1e3a2a] border-[#71e184] text-[#71e184]'
                          : 'bg-transparent border-[#515153] text-[#8f9196] hover:border-[#4a9a6a]'
                      }`}
                    >
                      <span className="font-medium truncate">{m.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* All conversations */}
          <div className="flex flex-col mb-2">
            {visibleConversations.map(conv => {
              const convId = conv._id ?? conv.id;
              const delCount = deleteCounters[convId] ?? 0;
              const isActive = activeConversationId === convId;
              const isRenaming = renamingId === convId;
              const createdDate = formatCreatedDate(conv.createdAt);
              const convMode = conv.chatMode ?? 'api';
              const modeColor = convMode === 'cloudcall' ? '#71e184' : convMode === 'webgpu' ? '#b07ae8' : '#6498CC';

              return (
                <div
                  key={convId}
                  className={`group relative flex items-start border-l-[3px] transition-colors ${
                    isActive
                      ? 'bg-[#323335] border-[#6498CC]'
                      : 'border-transparent hover:bg-[#2b2c2f]'
                  }`}
                >
                  {isRenaming ? (
                    <input
                      autoFocus
                      maxLength={40}
                      value={renameInput}
                      onChange={e => setRenameInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitRename(convId);
                        if (e.key === 'Escape') { setRenamingId(null); setRenameInput(''); }
                      }}
                      onBlur={() => commitRename(convId)}
                      className="flex-1 mx-2 my-1 px-2 py-0.5 bg-transparent border-b border-[#6498CC] text-[#d5d6d9] text-[13px] outline-none"
                    />
                  ) : (
                    <button
                      onClick={() => handleSelectConv(convId)}
                      className="flex-1 px-4 pt-2 pb-1.5 text-left min-w-0"
                    >
                      <div className={`text-[13px] truncate ${isActive ? 'text-[#d5d6d9]' : 'text-[#8f9196]'}`}>
                        {conv.title || 'Untitled Chat'}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {createdDate && (
                          <span className="text-[10px] text-[#515153]">{createdDate}</span>
                        )}
                        <span className="text-[9px] font-semibold" style={{ color: modeColor, opacity: 0.7 }}>
                          {convMode === 'cloudcall' ? '⚡' : convMode === 'webgpu' ? '◈' : '☁'}
                        </span>
                      </div>
                    </button>
                  )}

                  {!isRenaming && (
                    <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 pr-2 pt-2 flex-shrink-0 transition-opacity">
                      <button
                        onClick={(e) => startRename(e, convId, conv.title)}
                        className="p-1 text-[#8f9196] hover:text-[#d5d6d9] rounded"
                        title="Rename"
                      >
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => handleDelete(e, convId)}
                        className={`px-1 py-0.5 rounded text-[10px] font-bold transition-colors ${
                          delCount > 0
                            ? 'text-[#e17171] hover:text-white bg-[#4c2424]'
                            : 'text-[#8f9196] hover:text-[#e17171]'
                        }`}
                        title={delCount > 0 ? `${delCount}/4 — click ${4 - delCount} more to delete` : 'Delete'}
                      >
                        {delCount > 0 ? `${delCount}/4` : '×'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {hasMore && (
              <button
                onClick={() => setShownCount((n) => n + PAGE_SIZE_MORE)}
                className="mx-4 mt-1 py-1 text-[12px] text-[#6498CC] hover:text-[#7aaddd] text-left transition-colors"
              >
                Load more ({conversations.length - shownCount} remaining)
              </button>
            )}

            {conversations.length === 0 && (
              <div className="px-4 py-1 text-[#515153] text-[12px]">
                No chats yet — click + to start
              </div>
            )}
          </div>
        </nav>

        <div className="p-4 mt-auto border-t border-[#3e3f42]">
          <button
            onClick={() => navigate('settings')}
            className={`w-[30px] h-[30px] flex items-center justify-center text-[#b3b4b7] hover:text-white transition-colors rounded-[4px] border border-[#515153] ${activeView === 'settings' ? 'bg-[#515153] text-white' : 'bg-[#323335]'}`}
          >
            <svg className="w-[16px] h-[16px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.6V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
          </button>
        </div>
      </aside>
    </>
  );
}
